import type { Track } from 'livekit-client';
import { logger } from '#~/logger/index.js';

type Context = { instanceId: string; deviceId?: string | undefined };
type Fields = Record<string, unknown>;
const trackIds = new WeakMap<object, number>();
let nextTrackId = 0;

/** Compare preview instances without recording URLs, track labels, or credentials. */
export const videoAttachmentSnapshot = (
  element: HTMLVideoElement | null,
  track: Track | null | undefined
): Fields => {
  if (track && !trackIds.has(track)) trackIds.set(track, ++nextTrackId);
  const media = track?.mediaStreamTrack;
  const source = element?.srcObject;
  const sourceTracks = source && 'getVideoTracks' in source ? source.getVideoTracks() : [];
  return {
    trackObjectId: track ? trackIds.get(track) : undefined,
    trackSid: track?.sid,
    mediaStreamId: track?.mediaStreamID,
    mediaTrackId: media?.id,
    mediaReadyState: media?.readyState,
    mediaMuted: media?.muted,
    mediaEnabled: media?.enabled,
    streamState: track?.streamState,
    attachedElementCount: track?.attachedElements?.length,
    elementRegistered: element ? track?.attachedElements?.includes(element) : false,
    elementPresent: !!element,
    elementConnected: element?.isConnected,
    sourcePresent: !!source,
    sourceMatchesTrack: !!media && sourceTracks.includes(media),
    sourceTrackIds: sourceTracks.map((item) => item.id),
    readyState: element?.readyState,
    networkState: element?.networkState,
    paused: element?.paused,
    ended: element?.ended,
    currentTime: element?.currentTime,
    videoWidth: element?.videoWidth,
    videoHeight: element?.videoHeight,
    clientWidth: element?.clientWidth,
    clientHeight: element?.clientHeight,
    decodedFrames: element?.getVideoPlaybackQuality?.().totalVideoFrames,
    droppedFrames: element?.getVideoPlaybackQuality?.().droppedVideoFrames,
    mediaErrorCode: element?.error?.code,
    visibility: document.visibilityState
  };
};

export const traceVideoAttachment = (
  event: string,
  context: Context,
  element: HTMLVideoElement | null,
  track: Track | null | undefined,
  fields: Fields = {}
): void => {
  try {
    logger.info(
      `[video-attach-trace] ${JSON.stringify({
        event,
        at: new Date().toISOString(),
        ...context,
        ...videoAttachmentSnapshot(element, track),
        ...fields
      })}`
    );
  } catch {
    // A diagnostic failure must never disrupt playback or recovery.
  }
};

/** Observe each element independently; report transitions instead of every frame. */
export const observeVideoAttachment = (
  context: Context,
  element: HTMLVideoElement,
  track: Track | null | undefined
): (() => void) => {
  const emit = (event: string, fields: Fields = {}) =>
    traceVideoAttachment(event, context, element, track, fields);
  emit('observe');
  const onMediaEvent = (event: Event) => emit(`element.${event.type}`);
  const events = [
    'loadedmetadata',
    'loadeddata',
    'playing',
    'waiting',
    'stalled',
    'pause',
    'emptied',
    'error',
    'ended',
    'resize'
  ];
  for (const event of events) element.addEventListener(event, onMediaEvent);
  const media = track?.mediaStreamTrack;
  const onTrackEvent = (event: Event) => emit(`track.${event.type}`);
  for (const event of ['mute', 'unmute', 'ended']) media?.addEventListener?.(event, onTrackEvent);
  let lastTime = element.currentTime;
  let lastFrames = element.getVideoPlaybackQuality?.().totalVideoFrames;
  let lastHealth = '';
  const interval = setInterval(() => {
    const frames = element.getVideoPlaybackQuality?.().totalVideoFrames;
    const timeAdvanced = element.currentTime > lastTime;
    const framesAdvanced =
      frames === undefined || lastFrames === undefined ? undefined : frames > lastFrames;
    const snapshot = videoAttachmentSnapshot(element, track);
    const health = JSON.stringify([
      timeAdvanced,
      framesAdvanced,
      snapshot.sourceMatchesTrack,
      snapshot.mediaReadyState,
      snapshot.mediaMuted,
      snapshot.paused,
      snapshot.streamState,
      snapshot.visibility,
      snapshot.clientWidth,
      snapshot.clientHeight
    ]);
    if (health !== lastHealth) {
      emit('progress', { timeAdvanced, framesAdvanced, sampleMs: 5000 });
      lastHealth = health;
    }
    lastTime = element.currentTime;
    lastFrames = frames;
  }, 5000);
  return () => {
    clearInterval(interval);
    for (const event of events) element.removeEventListener(event, onMediaEvent);
    for (const event of ['mute', 'unmute', 'ended'])
      media?.removeEventListener?.(event, onTrackEvent);
    emit('unobserve');
  };
};
